import { useState, useEffect, useMemo, useCallback } from 'react';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { PageHeader } from '@/components/layout/PageHeader';
import { AcademyScheduleCalendar } from '@/components/schedule/AcademyScheduleCalendar';
import { ScheduleLocationFilter } from '@/components/schedule/ScheduleLocationFilter';
import { SportFilter } from '@/components/sport/SportFilter';
import { useAcademySports } from '@/hooks/useAcademySports';
import {
  ScheduleViewHeaderToggle,
  type ScheduleCalendarView,
} from '@/components/schedule/ScheduleViewHeaderToggle';
import { CoachLessonOccurrenceModal } from '@/components/coach/CoachLessonOccurrenceModal';
import { DayCourtScheduleView } from '@/components/schedule/DayCourtScheduleView';
import { coachService, type CoachScheduleData } from '@/services/coach';
import { TierFeatureKey } from '@court-sync/shared';
import { useCoachAcademyFeatureAccess } from '@/hooks/useFeatureAccess';
import { lessonMatchesScheduleLocationFilter } from '@/utils/lessonLocationFilter';
import { lessonMatchesSportFilter } from '@/utils/publicBrowseMode';
import {
  buildAcademyOccurrencesFromLessonSessions,
  scheduleOccurrenceToClickPayload,
  type LessonOccurrenceClickPayload,
  type RemovedFromSessionRef,
} from '@/utils/scheduleOccurrences';
import { isSameDay, subMonths, addMonths, startOfMonth, endOfMonth, format } from 'date-fns';
import { useCourts, usePublicLocations } from '@/hooks/useDataStore';
import { useLessonCalendarStore } from '@/store/lessonCalendar';

export default function CoachSchedulePage() {
    const { academyId, academy, academySlug } = useCoachAcademyStore();
    const { sports: academySports, isMultiSport } = useAcademySports(academy);
    const { hasAccess: hasMultiLocation } = useCoachAcademyFeatureAccess(
        academy,
        academySlug,
        TierFeatureKey.MULTI_LOCATION
    );
    const { courts } = useCourts(academyId ?? undefined);
    const { locations } = usePublicLocations(academyId ?? undefined, {
        enabled: hasMultiLocation,
    });
    const lessonSessions = useLessonCalendarStore((state) => state.lessonSessions);
    const fetchCalendarForRange = useLessonCalendarStore((state) => state.fetchForRange);
    const [scheduleLocationFilter, setScheduleLocationFilter] = useState('');
    const [sportFilter, setSportFilter] = useState('');
    const [scheduleView, setScheduleViewState] = useState<ScheduleCalendarView>(() => {
        try {
            const stored = localStorage.getItem('courtsync.coachSchedule.scheduleView');
            if (stored === 'monthly' || stored === 'weekly' || stored === 'day') return stored;
        } catch {
            /* localStorage unavailable */
        }
        return 'weekly';
    });
    const setScheduleView = useCallback((view: ScheduleCalendarView) => {
        setScheduleViewState(view);
        try {
            localStorage.setItem('courtsync.coachSchedule.scheduleView', view);
        } catch {
            /* localStorage unavailable */
        }
    }, []);
    const [schedule, setSchedule] = useState<CoachScheduleData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sessionModalOccurrence, setSessionModalOccurrence] =
        useState<LessonOccurrenceClickPayload | null>(null);
    const [selectedDayDate, setSelectedDayDate] = useState(() => new Date());

    const scheduleFetchRange = useMemo(() => {
        const start = startOfMonth(subMonths(new Date(), 6));
        const end = endOfMonth(addMonths(new Date(), 12));
        const startDate = format(start, 'yyyy-MM-dd');
        const endDate = format(end, 'yyyy-MM-dd');
        return { start, end, startDate, endDate };
    }, []);

    const refreshScheduleData = useCallback(async () => {
        if (!academyId) return;
        const { start, end, startDate, endDate } = scheduleFetchRange;
        const data = await coachService.getCoachSchedule(academyId, { startDate, endDate });
        setSchedule(data);
        await fetchCalendarForRange(academyId, start, end);
    }, [academyId, scheduleFetchRange, fetchCalendarForRange]);

    const locationsForUi = academyId && hasMultiLocation ? locations : [];

    useEffect(() => {
        if (!academyId) return;
        /* eslint-disable react-hooks/set-state-in-effect */
        setLoading(true);
        setError(null);
        /* eslint-enable react-hooks/set-state-in-effect */
        refreshScheduleData()
            .then(() => setError(null))
            .catch((err) => {
                setError(err instanceof Error ? err.message : 'Failed to load schedule');
                setSchedule(null);
            })
            .finally(() => setLoading(false));
    }, [academyId, refreshScheduleData]);

    useEffect(() => {
        if (!academyId) return undefined;
        const intervalId = window.setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            void refreshScheduleData();
        }, 20_000);
        return () => window.clearInterval(intervalId);
    }, [academyId, refreshScheduleData]);

    const pendingScheduleBookings = useMemo(() => {
        const bookings = schedule?.bookings ?? [];
        return bookings
            .filter((b) => b.status === 'pending')
            .map((b) => ({ lessonId: b.lessonId, sessionDate: b.sessionDate }));
    }, [schedule]);

    const scheduleEnrollmentBookings = useMemo(() => {
        const bookings = schedule?.bookings ?? [];
        return bookings
            .filter((b) => b.status === 'confirmed' || b.status === 'pending')
            .map((b) => ({
                lessonId: b.lessonId,
                bookingId: b.id,
                sessionDate: b.sessionDate?.split('T')[0],
            }));
    }, [schedule?.bookings]);

    const removedFromSessionEnrollments = useMemo((): RemovedFromSessionRef[] => {
        const bookings = schedule?.bookings ?? [];
        const lessonIdByBookingId = new Map(bookings.map((b) => [b.id, b.lessonId]));
        const out: RemovedFromSessionRef[] = [];
        for (const [key, records] of Object.entries(schedule?.attendance ?? {})) {
            const colonIdx = key.lastIndexOf(':');
            if (colonIdx === -1) continue;
            const bookingId = key.slice(0, colonIdx);
            const date = key.slice(colonIdx + 1);
            const lessonId = lessonIdByBookingId.get(bookingId);
            if (!lessonId) continue;
            for (const record of records) {
                if (record.status === 'cancelled') {
                    out.push({ lessonId, date, bookingId });
                }
            }
        }
        return out;
    }, [schedule?.attendance, schedule?.bookings]);

    const activeLocations = useMemo(
        () => locationsForUi.filter((l) => l.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
        [locationsForUi]
    );

    const allLessons = schedule?.lessons ?? [];

    const filteredLessons = useMemo(() => {
        let lessons = allLessons;
        if (hasMultiLocation && activeLocations.length > 0 && scheduleLocationFilter) {
            lessons = lessons.filter((l) =>
                lessonMatchesScheduleLocationFilter(l, scheduleLocationFilter, courts)
            );
        }
        if (isMultiSport && sportFilter) {
            lessons = lessons.filter((l) =>
                lessonMatchesSportFilter(l, sportFilter, academySports)
            );
        }
        return lessons;
    }, [
        allLessons,
        hasMultiLocation,
        activeLocations.length,
        scheduleLocationFilter,
        courts,
        isMultiSport,
        sportFilter,
        academySports,
    ]);

    const filteredLessonIds = useMemo(() => new Set(filteredLessons.map((l) => l.id)), [filteredLessons]);

    const filteredMakeupSessions = useMemo(() => {
        const m = schedule?.makeupSessions;
        if (!m) return undefined;
        if (filteredLessonIds.size === allLessons.length) return m;
        return m.filter((s) => filteredLessonIds.has(s.lessonId));
    }, [schedule?.makeupSessions, filteredLessonIds, allLessons.length]);

    const filteredReportedAbsences = useMemo(() => {
        const m = schedule?.reportedAbsences;
        if (!m) return undefined;
        if (filteredLessonIds.size === allLessons.length) return m;
        return m.filter((s) => filteredLessonIds.has(s.lessonId));
    }, [schedule?.reportedAbsences, filteredLessonIds, allLessons.length]);

    const filteredPendingScheduleBookings = useMemo(() => {
        if (filteredLessonIds.size === allLessons.length) return pendingScheduleBookings;
        return pendingScheduleBookings.filter((b) => filteredLessonIds.has(b.lessonId));
    }, [pendingScheduleBookings, filteredLessonIds, allLessons.length]);

    const filteredScheduleEnrollmentBookings = useMemo(() => {
        if (filteredLessonIds.size === allLessons.length) return scheduleEnrollmentBookings;
        return scheduleEnrollmentBookings.filter((b) => filteredLessonIds.has(b.lessonId));
    }, [scheduleEnrollmentBookings, filteredLessonIds, allLessons.length]);

    const filteredRemovedFromSessionEnrollments = useMemo(() => {
        if (filteredLessonIds.size === allLessons.length) return removedFromSessionEnrollments;
        return removedFromSessionEnrollments.filter((r) => filteredLessonIds.has(r.lessonId));
    }, [removedFromSessionEnrollments, filteredLessonIds, allLessons.length]);

    const courtNamesById = useMemo(() => {
        const fromApi = schedule?.courts;
        if (fromApi && Object.keys(fromApi).length > 0) return fromApi;
        return Object.fromEntries(courts.map((c) => [c.id, c.name] as const));
    }, [schedule?.courts, courts]);

    const locationScopedCourts = useMemo(() => {
        const active = courts.filter((c) => c.isActive !== false);
        if (hasMultiLocation && scheduleLocationFilter) {
            return active.filter((c) => c.locationId === scheduleLocationFilter);
        }
        return active;
    }, [courts, hasMultiLocation, scheduleLocationFilter]);

    const scheduleTerms = useMemo(
        () => (schedule ? Object.values(schedule.terms) : []),
        [schedule]
    );

    const dayScheduleOccurrences = useMemo(() => {
        if (!schedule?.coach.id) return [];
        const all = buildAcademyOccurrencesFromLessonSessions(
            filteredLessons,
            scheduleTerms,
            lessonSessions,
            filteredMakeupSessions,
            filteredPendingScheduleBookings,
            filteredReportedAbsences,
            courtNamesById,
            schedule.coach.id,
            'active',
            filteredScheduleEnrollmentBookings,
            filteredRemovedFromSessionEnrollments
        );
        return all.filter((occ) => isSameDay(occ.date, selectedDayDate));
    }, [
        schedule,
        filteredLessons,
        scheduleTerms,
        lessonSessions,
        filteredMakeupSessions,
        filteredPendingScheduleBookings,
        filteredReportedAbsences,
        courtNamesById,
        filteredScheduleEnrollmentBookings,
        filteredRemovedFromSessionEnrollments,
        selectedDayDate,
    ]);

    const scheduleHeaderActions = (
        <ScheduleViewHeaderToggle value={scheduleView} onChange={setScheduleView} />
    );

    const scheduleHeader = (
        <PageHeader
            title="My Schedule"
            help="Weekly calendar with sport, location, and lesson-type colour cues"
            actions={scheduleHeaderActions}
        />
    );

    if (!academyId) {
        return (
            <div className="contents">
                {scheduleHeader}
                <p className="text-muted-foreground text-center py-12">
                    Select an academy to view your schedule.
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="contents">
                {scheduleHeader}
                <p className="text-destructive text-center py-12">{error}</p>
            </div>
        );
    }

    const terms = scheduleTerms;
    const overrides = schedule?.overrides ?? [];

    return (
        <div className="contents">
            {scheduleHeader}

            {scheduleView === 'day' ? (
                <DayCourtScheduleView
                    date={selectedDayDate}
                    onDateChange={setSelectedDayDate}
                    courts={locationScopedCourts}
                    occurrences={dayScheduleOccurrences}
                    academyId={academyId}
                    editable={false}
                    onOccurrenceClick={(occ) =>
                        setSessionModalOccurrence(scheduleOccurrenceToClickPayload(occ))
                    }
                />
            ) : (
            <AcademyScheduleCalendar
                lessons={filteredLessons}
                terms={terms}
                overrides={overrides}
                courtNamesById={courtNamesById}
                lessonSessions={lessonSessions}
                scheduleForCoachId={schedule?.coach.id}
                makeupSessions={filteredMakeupSessions}
                reportedAbsenceSessions={filteredReportedAbsences}
                pendingScheduleBookings={filteredPendingScheduleBookings}
                scheduleEnrollmentBookings={filteredScheduleEnrollmentBookings}
                removedFromSessionEnrollments={filteredRemovedFromSessionEnrollments}
                loading={loading}
                scheduleView={scheduleView}
                onScheduleViewChange={setScheduleView}
                defaultView="weekly"
                scheduleToolbarEnd={
                    <>
                        {isMultiSport ? (
                            <SportFilter
                                value={sportFilter}
                                onChange={setSportFilter}
                                sports={academySports}
                            />
                        ) : null}
                        {hasMultiLocation && activeLocations.length > 0 ? (
                            <ScheduleLocationFilter
                                value={scheduleLocationFilter}
                                onChange={setScheduleLocationFilter}
                                locations={activeLocations}
                            />
                        ) : null}
                    </>
                }
                scheduleToolbarBelow={
                    (hasMultiLocation || isMultiSport) &&
                    filteredLessons.length === 0 &&
                    allLessons.length > 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No lessons match the current filters in the loaded date range.
                        </p>
                    ) : null
                }
                onLessonOccurrenceClick={(payload) => setSessionModalOccurrence(payload)}
                showSportGlyphsOnChips={isMultiSport}
                academySports={academySports}
                emptyContent={
                    !loading && allLessons.length === 0 ? (
                        <p className="text-muted-foreground text-center py-12">
                            No lessons scheduled. Check back later.
                        </p>
                    ) : undefined
                }
            />
            )}

            <CoachLessonOccurrenceModal
                isOpen={!!sessionModalOccurrence}
                occurrence={sessionModalOccurrence}
                academyId={academyId ?? ''}
                schedule={schedule}
                courtNamesById={courtNamesById}
                onClose={() => setSessionModalOccurrence(null)}
                onAttendanceSaved={async () => {
                    await refreshScheduleData();
                }}
            />
        </div>
    );
}
